import { useState, useEffect } from 'react';

interface ClientProfile {
  client_id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  client_phase: number;
  self_managed: boolean;
  program_start_date: string | null;
  goal_weight: number | null;
  target_weight_loss_per_week: number | null;
  created_at: string;
  assigned_coaches: any[];
}

interface ClientMetrics {
  date: string;
  calculated_score: number;
  // ... other metric fields
}

interface ClientGoal {
  goal_id: string;
  goal_name: string;
  status: string;
  goal_value: number;
  // ... other goal fields
}

interface ClientData {
  profile: ClientProfile;
  metrics: ClientMetrics | null;
  goals: ClientGoal[];
}

interface UseCoachClientViewResult {
  clientData: ClientData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Custom hook for coaches to view client dashboard data
 * Fetches profile, goals, and metrics with proper auth headers
 */
export function useCoachClientView(clientId: string): UseCoachClientViewResult {
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch all data in parallel
      const [profileRes, goalsRes, metricsRes] = await Promise.all([
        fetch(`/api/coach/clients/${clientId}`, { headers }),
        fetch(`/api/coach/clients/${clientId}/goals`, { headers }),
        fetch(`/api/coach/clients/${clientId}/metrics/latest`, { headers })
      ]);

      // Check for errors
      if (!profileRes.ok || !goalsRes.ok || !metricsRes.ok) {
        throw new Error('Failed to fetch client data');
      }

      const [profileData, goalsData, metricsData] = await Promise.all([
        profileRes.json(),
        goalsRes.json(),
        metricsRes.json()
      ]);

      if (!profileData.success || !goalsData.success || !metricsData.success) {
        throw new Error('API returned error response');
      }

      setClientData({
        profile: profileData.client,
        metrics: metricsData.metrics,
        goals: goalsData.goals
      });
    } catch (err) {
      console.error('Error loading client data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadClientData();
    }
  }, [clientId]);

  return {
    clientData,
    loading,
    error,
    refresh: loadClientData
  };
}
